import React, { useEffect, useState } from 'react';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { decideDuplicate, errorMessage } from '../../api/catalogImportApi';
import VariantsEditor from './VariantsEditor';
import { emptyRow, fromRows, rowsError, single } from './variantRows';
import { btnGhost, btnPrimary, card, input, label, overlay } from './styles';

const inr = (v) => (v === null || v === undefined ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);

/**
 * "This draft is another option of an existing product": the reviewer lists the option(s) to add
 * — colour/size, own price, stock, photo — and they're appended to the product's options. A
 * product without options first gets its current version described as option 1, so the product
 * page has something to switch between. Nothing is generated automatically.
 */
export default function AddVariantModal({ item, productId, campaign, onClose, onDone }) {
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [current, setCurrent] = useState(null); // option row describing a product with no options yet
  const [rows, setRows] = useState(() => [emptyRow({
    color: single(item.colors), size: single(item.sizes), mrp: item.mrp ?? '',
    eyantraPurchasePrice: item.eyantraPurchasePrice ?? '', imageId: item.images?.[0]?.id ?? '',
  })]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/products/${productId}`)
      .then((r) => {
        setProduct(r.data);
        if (!(r.data.variants || []).length) {
          setCurrent(emptyRow({ color: single(r.data.colors), size: single(r.data.sizes), mrp: r.data.mrp ?? '',
            eyantraPurchasePrice: r.data.eyantraPurchasePrice ?? '' }));
        }
      })
      .catch((err) => setLoadError(errorMessage(err, 'Could not load the existing product.')));
  }, [productId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const existing = product?.variants || [];
  const tracksStock = existing.some((v) => v.stockQty !== null && v.stockQty !== undefined);

  const submit = async () => {
    const msg = rowsError(rows) || (current && rowsError([current]));
    if (msg) { setError(msg); return; }
    setSaving(true);
    setError('');
    try {
      const body = { decision: 'ADD_VARIANT', productId, variants: fromRows(rows) };
      if (current) body.currentAsVariant = fromRows([current])[0];
      const res = await decideDuplicate(item.id, body);
      onDone(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not add the option.'));
      setSaving(false);
    }
  };

  const setCurrentField = (key) => (e) => setCurrent((c) => ({ ...c, [key]: e.target.value }));

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label={`Add as an option of product #${productId}`}>
      <div style={{ ...card, width: 'min(1000px, 100%)', maxHeight: '92vh', overflowY: 'auto', marginBottom: 0 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700 }}>Add as an option of product #{productId}</h3>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6B7280' }}>
          No new product is created — the option{rows.length > 1 ? 's are' : ' is'} added to {product ? <strong>{product.name}</strong> : 'the existing product'}
          {campaign && <>, which is then added to <strong>{campaign.name}</strong></>}.
        </p>
        <div role="note" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#92400E', marginBottom: 12 }}>
          The product changes everywhere it's sold — the main shop and every Brandstore.
          {user?.role !== 'ADMIN' && <> <strong>As a curator, this sends the product back for admin approval; it's hidden until approved.</strong></>}
        </div>

        {error && <div role="alert" style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10 }}>{error}</div>}

        {loadError ? <p role="alert" style={{ color: '#B91C1C' }}>{loadError}</p> : !product ? <p style={{ color: '#9CA3AF' }}>Loading…</p> : (
          <>
            <h4 style={{ fontSize: 13, margin: '0 0 6px' }}>Options it has now</h4>
            {existing.length > 0 ? (
              <ul style={{ margin: '0 0 14px', paddingLeft: 18, fontSize: 13, color: '#374151' }}>
                {existing.map((v) => (
                  <li key={v.id}>{v.label} — {inr(v.mrp)}{v.stockQty !== null && v.stockQty !== undefined ? ` · ${v.stockQty} in stock` : ''}</li>
                ))}
              </ul>
            ) : current && (
              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
                <p style={{ fontSize: 12, color: '#4B5563', margin: '0 0 8px' }}>
                  It has no options yet. Describe the version it sells today — it becomes option 1, keeping the current photo.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                  {[['color', 'Colour'], ['size', 'Size'], ['mrp', 'MRP ₹'], ['eyantraPurchasePrice', 'EPP ₹'], ['sku', 'SKU'], ['stockQty', 'Stock']].map(([k, t]) => (
                    <div key={k}>
                      <label style={label} htmlFor={`cur-${k}`}>{t}</label>
                      <input id={`cur-${k}`} style={input} value={current[k]} onChange={setCurrentField(k)} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h4 style={{ fontSize: 13, margin: '0 0 6px' }}>Option{rows.length > 1 ? 's' : ''} to add from this catalog</h4>
            {tracksStock && <p style={{ fontSize: 12, color: '#B45309', margin: '0 0 6px' }}>This product tracks stock per option — enter a stock count for each new one.</p>}
            <VariantsEditor rows={rows} onChange={setRows} images={item.images || []} disabled={saving}
                            newRow={{ mrp: item.mrp ?? '' }} mopSet={product.marketOperatingPrice != null} idPrefix={`addvar-${item.id}`} />
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button style={btnGhost} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...btnPrimary, opacity: saving || !product || rows.length === 0 ? 0.6 : 1 }}
                  disabled={saving || !product || rows.length === 0} onClick={submit}>
            {saving ? 'Adding…' : `Add option${rows.length > 1 ? 's' : ''}${campaign ? ' & add to campaign' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
